import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import { formatCurrency, panelSurfaceSx } from './controlCenterUtils';
import ControlCenterInfoTooltip from './ControlCenterInfoTooltip';

const SHARED_FILTERS =
  'Also limited by the Control Center client filter (if any) and the selected date range.';

/** User-facing filter explanations aligned with controlCenterDao overview universes. */
export const JOBS_SUMMARY_INFO = {
  created:
    `Counts every job whose create date falls in the selected range, for the selected clients (or all clients). Includes every status, including Cancelled. ${SHARED_FILTERS}`,
  completed:
    `Counts jobs whose current status is Recall No Charge, Invoiced, Cancelled, or Invoiced/Loss, and whose completion date falls in the selected range. Completion date = invoice date for Invoiced / Invoiced-Loss; lock date for Recall No Charge / Cancelled. ${SHARED_FILTERS}`,
  invoiced:
    `Counts jobs currently Invoiced whose proposal invoice date falls in the selected range (selected clients or all). ${SHARED_FILTERS}`,
  invoiced_or_loss:
    `Counts jobs currently Invoiced or Invoiced/Loss whose proposal invoice date falls in the selected range (selected clients or all). ${SHARED_FILTERS}`,
  average_job_size:
    `Average NTE of the same jobs as Job Completed (same statuses and completion-date rules). Cancelled / Recall No Charge are included (NTE is often $0). Not based on proposal price. ${SHARED_FILTERS}`,
  invoiced_or_loss_jobs:
    `Revenue, cost, and profit for the same jobs as the Jobs Invoiced / Invoiced-loss count (Invoiced or Invoiced/Loss, dated by invoice date). ${SHARED_FILTERS}`,
  completed_jobs:
    `Revenue, cost, and profit for the same jobs as Job Completed (hybrid completion date; includes Cancelled / Recall No Charge). ${SHARED_FILTERS}`,
  revenue:
    'Sum of NTE for jobs in this group’s universe. Not proposal price; not the paid amount.',
  cost:
    'Sum of all technician costs plus all parts costs on those jobs (no paid-date or parts-approval filter).',
  profits: 'Revenue minus Cost for this group.',
};

const LabelWithInfo = ({ label, info, sx }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 0,
      ...sx,
    }}
  >
    <Typography component="span" sx={{ fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit' }}>
      {label}
    </Typography>
    {info ? <ControlCenterInfoTooltip title={info} /> : null}
  </Box>
);

const MetricBlock = ({ label, value, info, onClick }) => {
  const clickable = typeof onClick === 'function';

  return (
    <Box
      sx={{
        minWidth: 0,
        flex: 1,
        textAlign: 'left',
      }}
    >
      <Typography
        sx={{
          fontSize: 13,
          color: 'text.secondary',
          mb: 0.5,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <LabelWithInfo label={label} info={info} />
      </Typography>
      <Box
        component={clickable ? 'button' : 'div'}
        type={clickable ? 'button' : undefined}
        onClick={onClick}
        sx={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          border: 'none',
          bgcolor: 'transparent',
          color: 'text.primary',
          p: 0,
          m: 0,
          cursor: clickable ? 'pointer' : 'default',
          borderRadius: 1,
          '&:hover': clickable
            ? {
                opacity: 0.85,
                '& .cc-kpi-value': { textDecorationColor: 'currentColor' },
              }
            : undefined,
          '&:focus-visible': clickable
            ? {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              }
            : undefined,
        }}
      >
        <Typography
          className="cc-kpi-value"
          sx={{
            fontSize: { xs: 22, sm: 28 },
            fontWeight: 600,
            lineHeight: 1.2,
            ...(clickable
              ? {
                  textDecoration: 'underline',
                  textDecorationColor: 'transparent',
                  transition: 'text-decoration-color 0.15s',
                }
              : {}),
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const FinancialMetric = ({ label, value, info, valueSx, showDivider }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: { xs: 1, md: 1.5 },
        borderLeft: showDivider ? '1px solid' : 'none',
        borderColor: isDark ? '#334155' : '#e7e7e7',
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          color: 'text.secondary',
          mb: 0.25,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <LabelWithInfo label={label} info={info} />
      </Typography>
      <Typography sx={{ fontSize: { xs: 14, md: 16 }, fontWeight: 600, ...valueSx }}>
        {value}
      </Typography>
    </Box>
  );
};

const FinancialGroup = ({
  title,
  titleInfo,
  revenue,
  cost,
  profit,
  showTopBorder,
  onClick,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const clickable = typeof onClick === 'function';

  const handleKeyDown = (e) => {
    if (!clickable) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(e);
    }
  };

  return (
    <Box
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      sx={{
        flex: 1,
        minWidth: 0,
        textAlign: 'left',
        cursor: clickable ? 'pointer' : 'default',
        borderRadius: 1,
        pt: { xs: showTopBorder ? 1.25 : 0, md: 0 },
        borderTop: {
          xs: showTopBorder ? '1px solid' : 'none',
          md: 'none',
        },
        borderColor: isDark ? '#334155' : '#e7e7e7',
        '&:hover': clickable
          ? {
              opacity: 0.85,
              '& .cc-financial-title': { textDecorationColor: 'currentColor' },
            }
          : undefined,
        '&:focus-visible': clickable
          ? {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2,
            }
          : undefined,
      }}
    >
      <Typography
        className="cc-financial-title"
        sx={{
          fontSize: 13,
          fontWeight: 500,
          color: 'text.secondary',
          mb: 1,
          px: { xs: 1, md: 1.5 },
          display: 'flex',
          alignItems: 'center',
          ...(clickable
            ? {
                textDecoration: 'underline',
                textDecorationColor: 'transparent',
                transition: 'text-decoration-color 0.15s',
              }
            : {}),
        }}
      >
        <LabelWithInfo label={title} info={titleInfo} />
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <FinancialMetric
          label="Revenue"
          info={JOBS_SUMMARY_INFO.revenue}
          value={formatCurrency(revenue)}
        />
        <FinancialMetric
          label="Cost"
          info={JOBS_SUMMARY_INFO.cost}
          value={formatCurrency(cost)}
          valueSx={{ color: 'error.main' }}
          showDivider
        />
        <FinancialMetric
          label="Profits"
          info={JOBS_SUMMARY_INFO.profits}
          value={formatCurrency(profit)}
          valueSx={{ color: profit >= 0 ? 'success.main' : 'error.main' }}
          showDivider
        />
      </Box>
    </Box>
  );
};

export default function JobsSummaryPanel({
  data,
  loading,
  error,
  rangeLabel,
  onMetricClick,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const summary = data?.jobsSummary;

  const handleMetric = (metric) => {
    if (typeof onMetricClick === 'function') onMetricClick(metric);
  };

  return (
    <Box sx={panelSurfaceSx(theme)}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: { xs: 1.5, sm: 2.5 },
          pt: { xs: 1.5, sm: 2 },
          pb: 1,
        }}
      >
        <Typography sx={{ fontSize: 15, fontWeight: 500, color: 'text.secondary' }}>
          Jobs
        </Typography>
        {rangeLabel && (
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{rangeLabel}</Typography>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {!loading && !error && summary && (
        <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pb: { xs: 1.5, sm: 2 } }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: { xs: 2, sm: 3 },
              mb: 1.5,
            }}
          >
            <MetricBlock
              label="Jobs Created"
              info={JOBS_SUMMARY_INFO.created}
              value={summary.created ?? 0}
              onClick={() => handleMetric('created')}
            />
            <MetricBlock
              label="Job Completed"
              info={JOBS_SUMMARY_INFO.completed}
              value={summary.completed ?? 0}
              onClick={() => handleMetric('completed')}
            />
            <MetricBlock
              label="Jobs Invoiced"
              info={JOBS_SUMMARY_INFO.invoiced}
              value={summary.invoicedCount ?? 0}
              onClick={() => handleMetric('invoiced')}
            />
            <MetricBlock
              label="Jobs Invoiced / Invoiced-loss"
              info={JOBS_SUMMARY_INFO.invoiced_or_loss}
              value={summary.invoicedOrLossCount ?? 0}
              onClick={() => handleMetric('invoiced_or_loss')}
            />
            <MetricBlock
              label="Average Job size"
              info={JOBS_SUMMARY_INFO.average_job_size}
              value={formatCurrency(summary.averageJobSize, { fractionDigits: 0 })}
              onClick={() => handleMetric('average_job_size')}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 0, md: 3 },
              pt: 1.25,
              borderTop: '1px solid',
              borderColor: isDark ? '#334155' : '#e7e7e7',
            }}
          >
            <FinancialGroup
              title="Invoiced / Invoiced-Loss Jobs"
              titleInfo={JOBS_SUMMARY_INFO.invoiced_or_loss_jobs}
              revenue={summary.invoicedOrLossJobs?.revenue}
              cost={summary.invoicedOrLossJobs?.cost}
              profit={summary.invoicedOrLossJobs?.profit}
              onClick={() => handleMetric('invoiced_or_loss')}
            />
            <FinancialGroup
              title="Completed Jobs"
              titleInfo={JOBS_SUMMARY_INFO.completed_jobs}
              revenue={summary.completedJobs?.revenue}
              cost={summary.completedJobs?.cost}
              profit={summary.completedJobs?.profit}
              showTopBorder
              onClick={() => handleMetric('completed')}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}
